// Site colors and fonts. A theme is a handful of values that become CSS
// variables on the page; components read the variables.
import { CSSProperties } from 'react';
import { FONTS, FontId, isFontId } from './fonts';

export interface Theme {
  preset?: string;
  pageBackground: string;
  text: string;
  cardBackground: string;
  cardText: string;
  accent: string;
  footerBackground: string;
  headingFont: FontId;
  bodyFont: FontId;
  backgroundImage?: string;
}

export type ColorKey = 'pageBackground' | 'text' | 'cardBackground' | 'cardText' | 'accent' | 'footerBackground';

export const COLOR_FIELDS: { key: ColorKey; label: string; hint: string }[] = [
  { key: 'pageBackground', label: 'Page background', hint: 'Behind everything on the page' },
  { key: 'text', label: 'Page text', hint: 'Headings and text on the page background' },
  { key: 'cardBackground', label: 'Card background', hint: 'Show, band member and photo cards' },
  { key: 'cardText', label: 'Card text', hint: 'Text inside cards' },
  { key: 'accent', label: 'Buttons', hint: 'Buttons like "Book Us"' },
  { key: 'footerBackground', label: 'Footer', hint: 'The bar with the social icons' },
];

/** The original Arc Flash look. */
const DARK: Theme = {
  preset: 'dark',
  pageBackground: '#212529',
  text: '#f8f9fa',
  cardBackground: '#ffffff',
  cardText: '#212529',
  accent: '#f8f9fa',
  footerBackground: '#000000',
  headingFont: 'sour-gummy',
  bodyFont: 'sour-gummy',
};

export const PRESETS: { id: string; label: string; theme: Theme }[] = [
  { id: 'dark', label: 'Original', theme: DARK },
  {
    id: 'light',
    label: 'Light',
    theme: { preset: 'light', pageBackground: '#f4f4f2', text: '#1b1b1f', cardBackground: '#ffffff', cardText: '#1b1b1f', accent: '#1b1b1f', footerBackground: '#1b1b1f', headingFont: 'oswald', bodyFont: 'inter' },
  },
  {
    id: 'midnight',
    label: 'Midnight',
    theme: { preset: 'midnight', pageBackground: '#0b1d2a', text: '#e8f1f8', cardBackground: '#13314a', cardText: '#e8f1f8', accent: '#4cc9f0', footerBackground: '#06121b', headingFont: 'bebas-neue', bodyFont: 'montserrat' },
  },
  {
    id: 'ember',
    label: 'Ember',
    theme: { preset: 'ember', pageBackground: '#1a0f0a', text: '#fbe9d7', cardBackground: '#2d1a12', cardText: '#fbe9d7', accent: '#ff7a18', footerBackground: '#0d0705', headingFont: 'permanent-marker', bodyFont: 'roboto-slab' },
  },
  {
    id: 'neon',
    label: 'Neon',
    theme: { preset: 'neon', pageBackground: '#0d0d0d', text: '#f2f2f2', cardBackground: '#1a1a1a', cardText: '#f2f2f2', accent: '#39ff14', footerBackground: '#000000', headingFont: 'oswald', bodyFont: 'inter' },
  },
];

export const DEFAULT_THEME = DARK;

const HEX = /^#[0-9a-f]{6}$/i;
export const isHexColor = (value: unknown): value is string => typeof value === 'string' && HEX.test(value);

/** Fills in anything missing or invalid from the default theme. */
export function normalizeTheme(input: unknown): Theme {
  const raw = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;
  const theme: Theme = { ...DEFAULT_THEME, preset: typeof raw.preset === 'string' ? raw.preset : undefined };
  COLOR_FIELDS.forEach(({ key }) => {
    if (isHexColor(raw[key])) theme[key] = (raw[key] as string).toLowerCase();
  });
  if (isFontId(raw.headingFont)) theme.headingFont = raw.headingFont;
  if (isFontId(raw.bodyFont)) theme.bodyFont = raw.bodyFont;
  if (typeof raw.backgroundImage === 'string' && raw.backgroundImage.startsWith('/') && !raw.backgroundImage.startsWith('//')) {
    theme.backgroundImage = raw.backgroundImage;
  }
  if (!raw.preset && raw.pageBackground === undefined) theme.preset = DEFAULT_THEME.preset;
  return theme;
}

function luminance(hex: string): number {
  const channel = (i: number) => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
}

/** WCAG contrast ratio between two #rrggbb colors (1 to 21). */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/** Black or white, whichever is easier to read on the given color. */
export const readableOn = (hex: string) => (contrastRatio(hex, '#000000') >= contrastRatio(hex, '#ffffff') ? '#000000' : '#ffffff');

export const isDark = (hex: string) => readableOn(hex) === '#ffffff';

/** Normal-size text needs 4.5:1 to be readable (WCAG AA). */
export const MIN_CONTRAST = 4.5;

export interface ThemeProblem {
  colors: [ColorKey, ColorKey];
  message: string;
}

/** Optional styling for one section's box. Anything unset follows the page theme. */
export interface SectionStyle {
  background?: string;
  text?: string;
  /** Colors for the cards inside this section (shows, members, photos). */
  cardBackground?: string;
  cardText?: string;
  backgroundImage?: string;
  width?: 'full' | 'boxed';
  corners?: 'square' | 'rounded';
  spacing?: 'none' | 'compact' | 'normal' | 'roomy';
  border?: boolean;
}

const pick = <T extends string>(value: unknown, allowed: readonly T[]): T | undefined =>
  typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : undefined;

/** Keeps only valid values; returns undefined when nothing is styled. */
export function normalizeSectionStyle(input: unknown): SectionStyle | undefined {
  if (!input || typeof input !== 'object') return undefined;
  const raw = input as Record<string, unknown>;
  const style: SectionStyle = {};
  if (isHexColor(raw.background)) style.background = raw.background.toLowerCase();
  if (isHexColor(raw.text)) style.text = raw.text.toLowerCase();
  if (isHexColor(raw.cardBackground)) style.cardBackground = raw.cardBackground.toLowerCase();
  if (isHexColor(raw.cardText)) style.cardText = raw.cardText.toLowerCase();
  if (typeof raw.backgroundImage === 'string' && raw.backgroundImage.startsWith('/') && !raw.backgroundImage.startsWith('//')) {
    style.backgroundImage = raw.backgroundImage;
  }
  const width = pick(raw.width, ['full', 'boxed'] as const);
  const corners = pick(raw.corners, ['square', 'rounded'] as const);
  const spacing = pick(raw.spacing, ['none', 'compact', 'normal', 'roomy'] as const);
  if (width) style.width = width;
  if (corners) style.corners = corners;
  if (spacing) style.spacing = spacing;
  if (raw.border === true) style.border = true;
  return Object.keys(style).length ? style : undefined;
}

/** The text color a styled section actually uses. */
export function sectionTextColor(style: SectionStyle, theme: Theme): string {
  if (style.text) return style.text;
  return style.background ? readableOn(style.background) : theme.text;
}

export interface LayoutProblem {
  message: string;
  sectionId?: string;
}

/** Everything that blocks publishing: unreadable theme colors and unreadable section colors. */
export function layoutProblems(layout: { theme: Theme; sections: { id: string; title?: string; type: string; style?: SectionStyle }[] }): LayoutProblem[] {
  const problems: LayoutProblem[] = themeProblems(layout.theme).map(({ message }) => ({ message }));
  layout.sections.forEach((section) => {
    const style = section.style;
    if (!style) return;
    const name = section.title || section.type;
    if (style.text) {
      const ratio = contrastRatio(style.text, style.background ?? layout.theme.pageBackground);
      if (ratio < MIN_CONTRAST) {
        problems.push({ sectionId: section.id, message: `Text in the "${name}" section is hard to read (contrast ${ratio.toFixed(1)}:1, needs ${MIN_CONTRAST}:1).` });
      }
    }
    if (style.cardBackground || style.cardText) {
      const ratio = contrastRatio(style.cardText ?? layout.theme.cardText, style.cardBackground ?? layout.theme.cardBackground);
      if (ratio < MIN_CONTRAST) {
        problems.push({ sectionId: section.id, message: `Card text in the "${name}" section is hard to read (contrast ${ratio.toFixed(1)}:1, needs ${MIN_CONTRAST}:1).` });
      }
    }
  });
  return problems;
}

const SPACING = { none: '0', compact: '1rem', normal: '2rem', roomy: '3.5rem' } as const;

/** Inline styles for a section's box. */
export function sectionBoxStyle(style: SectionStyle, theme: Theme, imageSrc?: string): CSSProperties {
  const text = sectionTextColor(style, theme);
  const tint = style.background ?? theme.pageBackground;
  const out: Record<string, string> = {
    '--af-text': text,
    '--af-page-link': text,
    color: text,
    padding: `${SPACING[style.spacing ?? (style.background || style.backgroundImage || style.border ? 'normal' : 'none')]} 1rem`,
  };
  if (style.background) out.backgroundColor = style.background;
  // Text that sits on its own overlay (the bio) only changes when a color is chosen.
  if (style.text) out['--af-section-text'] = style.text;
  if (style.cardBackground || style.cardText) {
    const cardBg = style.cardBackground ?? theme.cardBackground;
    const cardText = style.cardText ?? theme.cardText;
    out['--af-card-bg'] = cardBg;
    out['--af-card-text'] = cardText;
    out['--af-card-link'] = contrastRatio(theme.accent, cardBg) >= MIN_CONTRAST ? theme.accent : cardText;
  }
  if (imageSrc) {
    out.backgroundImage = `linear-gradient(${hexToRgba(tint, 0.72)}, ${hexToRgba(tint, 0.72)}), url("${encodeURI(imageSrc)}")`;
    out.backgroundSize = 'cover';
    out.backgroundPosition = 'center';
  }
  if (style.corners === 'rounded') out.borderRadius = '1rem';
  if (style.border) out.border = `1px solid ${hexToRgba(text, 0.35)}`;
  return out as CSSProperties;
}

/** Color pairs that would be hard to read. Publishing is blocked while any exist. */
export function themeProblems(theme: Theme): ThemeProblem[] {
  const problems: ThemeProblem[] = [];
  const check = (fg: ColorKey, bg: ColorKey, what: string) => {
    const ratio = contrastRatio(theme[fg], theme[bg]);
    if (ratio < MIN_CONTRAST) {
      problems.push({ colors: [fg, bg], message: `${what} is hard to read (contrast ${ratio.toFixed(1)}:1, needs ${MIN_CONTRAST}:1).` });
    }
  };
  check('text', 'pageBackground', 'Page text on the page background');
  check('cardText', 'cardBackground', 'Card text on the card background');
  return problems;
}

export function hexToRgba(hex: string, alpha: number) {
  const n = (i: number) => parseInt(hex.slice(i, i + 2), 16);
  return `rgba(${n(1)}, ${n(3)}, ${n(5)}, ${alpha})`;
}

/**
 * CSS variables for the page. `imageSrc` resolves the background photo path
 * (in the editor it may be a local preview of a photo not yet published).
 */
export function themeStyle(theme: Theme, imageSrc?: string): CSSProperties {
  const vars: Record<string, string> = {
    '--af-page-bg': theme.pageBackground,
    '--af-text': theme.text,
    '--af-card-bg': theme.cardBackground,
    '--af-card-text': theme.cardText,
    '--af-card-link': contrastRatio(theme.accent, theme.cardBackground) >= MIN_CONTRAST ? theme.accent : theme.cardText,
    '--af-page-link': contrastRatio(theme.accent, theme.pageBackground) >= MIN_CONTRAST ? theme.accent : theme.text,
    '--af-accent': theme.accent,
    '--af-accent-text': readableOn(theme.accent),
    '--af-footer-bg': theme.footerBackground,
    '--af-footer-text': readableOn(theme.footerBackground),
    '--af-heading-font': FONTS[theme.headingFont].stack,
    '--af-body-font': FONTS[theme.bodyFont].stack,
  };
  // A photo behind the page gets a tint of the background color so text stays readable.
  vars['--af-page-image'] = imageSrc
    ? `linear-gradient(${hexToRgba(theme.pageBackground, 0.78)}, ${hexToRgba(theme.pageBackground, 0.78)}), url("${encodeURI(imageSrc)}")`
    : 'none';
  return vars as CSSProperties;
}
